import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { PartyPopper, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const BirthdayWishes = ({ dogName, birthDate }) => {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  const load = useCallback(
    () => api.get(`/dogs/${encodeURIComponent(dogName)}/wishes`).then((r) => setData(r.data)).catch(() => {}),
    [dogName]
  );

  useEffect(() => {
    if (birthDate) load();
  }, [birthDate, load]);

  if (!data || (!data.is_today && data.wishes.length === 0)) return null;

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/dogs/${encodeURIComponent(dogName)}/wishes`, { text });
      toast.success(`Auguri inviati a ${dogName}!`);
      setText("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const renderWishAction = () => {
    if (!data.is_today) return null;
    if (!user) {
      return (
        <p className="mt-4 text-sm text-[#8C7B6E]">
          <Link to="/accedi" data-testid="birthday-wish-login" className="font-semibold text-[#B85328] underline underline-offset-4">Accedi</Link> per fare gli auguri con un clic.
        </p>
      );
    }
    if (data.already_wished) {
      return <p data-testid="birthday-wish-done" className="mt-4 text-sm font-semibold text-[#4A6B5D]">Hai già fatto gli auguri a {dogName}. Grazie!</p>;
    }
    return (
      <form data-testid="birthday-wish-form" onSubmit={send} className="mt-5 flex flex-col gap-3 sm:flex-row">
        <input
          data-testid="birthday-wish-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={140}
          placeholder={`Buon compleanno, ${dogName}! (facoltativo)`}
          className="flex-1 rounded-full border border-[#E8DFD5] bg-white px-5 py-3 text-sm text-[#2C2723] outline-none transition-all placeholder:text-[#8C7B6E]/60 focus:border-[#B85328] focus:ring-2 focus:ring-[#B85328]/15"
        />
        <button
          type="submit"
          data-testid="birthday-wish-submit"
          disabled={busy}
          className="flex items-center justify-center gap-2 rounded-full bg-[#B85328] px-6 py-3 text-sm font-semibold text-[#FBF8F3] transition-all hover:bg-[#9C421C] disabled:opacity-60"
        >
          <Send size={14} /> Fai gli auguri
        </button>
      </form>
    );
  };

  return (
    <section data-testid="birthday-wishes" className="mt-8 rounded-3xl border border-[#B85328]/30 bg-[#FFF6EF] p-6 sm:p-8">
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#B85328]">
        <PartyPopper size={13} /> {data.is_today ? `Oggi è il compleanno di ${dogName}!` : `Gli auguri del ${data.year}`}
      </p>
      <p data-testid="birthday-wishes-count" className="font-display mt-2 text-2xl font-semibold text-[#2C2723]">
        {data.wishes.length === 0 ? "Sii il primo a fare gli auguri" : `${data.wishes.length} ${data.wishes.length === 1 ? "augurio" : "auguri"} dalla community`}
      </p>

      {renderWishAction()}

      {data.wishes.length > 0 && (
        <ul data-testid="birthday-wishes-list" className="mt-5 flex flex-wrap gap-2">
          {data.wishes.map((w) => (
            <li key={w.wish_id} data-testid={`birthday-wish-${w.wish_id}`} className="rounded-full border border-[#E8DFD5] bg-white px-4 py-2 text-sm text-[#2C2723]">
              <span className="font-semibold text-[#B85328]">{w.author_name}</span> · {w.text}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
